import { useCallback, useEffect, useRef, useState } from "react";
import { IntentKey, OperationResult } from "../../../../../database/mediaOps/mediaOpsApi";

// One user intent = one IntentKey. A retry after a transport error reuses the
// key; any server answer, or an edit (call `edited`), mints a new one.
//
// A panel with several actions or targets passes a scope to `run`, such as
// "retry:t1" or "approve:acct_ig". Each scope keeps its own key, so a retry
// after a transport error on one target never reuses the key of another
// target or action. No scope means the panel's single default key.

export type MutationState<T> = {
  busy: boolean;
  result: OperationResult<T> | null;
  run: (fn: (key: IntentKey) => Promise<OperationResult<T>>, scope?: string) => Promise<OperationResult<T> | null>;
  /** The form changed: the next submit is a new intent. No scope resets every key. */
  edited: (scope?: string) => void;
  clear: () => void;
  /** The default (unscoped) key. */
  key: IntentKey;
  /** The key for one (action, target) scope. */
  keyFor: (scope: string) => IntentKey;
};

const DEFAULT_SCOPE = "";

export const useMutation = <T = any>(): MutationState<T> => {
  const keysRef = useRef<Map<string, IntentKey>>(new Map([[DEFAULT_SCOPE, new IntentKey()]]));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OperationResult<T> | null>(null);
  const busyRef = useRef(false);

  const keyFor = useCallback((scope: string) => {
    let k = keysRef.current.get(scope);
    if (!k) {
      k = new IntentKey();
      keysRef.current.set(scope, k);
    }
    return k;
  }, []);

  const run = useCallback(
    async (fn: (key: IntentKey) => Promise<OperationResult<T>>, scope: string = DEFAULT_SCOPE) => {
      if (busyRef.current) return null;
      busyRef.current = true;
      setBusy(true);
      try {
        const res = await fn(keyFor(scope));
        setResult(res);
        return res;
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [keyFor]
  );

  const edited = useCallback((scope?: string) => {
    if (scope === undefined) keysRef.current.forEach((k) => k.reset());
    else keysRef.current.get(scope)?.reset();
  }, []);
  const clear = useCallback(() => setResult(null), []);

  return { busy, result, run, edited, clear, key: keyFor(DEFAULT_SCOPE), keyFor };
};

/** Loads a read op on mount and when deps change. */
export const useOpRead = <T = any>(
  load: () => Promise<OperationResult<T>> | null,
  deps: unknown[]
): { result: OperationResult<T> | null; loading: boolean; reload: () => void } => {
  const [result, setResult] = useState<OperationResult<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    const p = load();
    if (!p) {
      setResult(null);
      return undefined;
    }
    setLoading(true);
    p.then((r) => {
      if (alive) setResult(r);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { result, loading, reload };
};

/** Copies text. Uses the async clipboard, then a hidden textarea fallback. */
export const copyText = async (text: string): Promise<boolean> => {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall back below
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = typeof document.execCommand === "function" ? document.execCommand("copy") : false;
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Downloads files one after another so the browser does not drop any. */
export const downloadSequentially = async (
  files: { url: string; fileName: string }[],
  gapMs = 700
): Promise<number> => {
  let n = 0;
  for (const f of files) {
    const a = document.createElement("a");
    a.href = f.url;
    a.download = f.fileName;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    n += 1;
    if (n < files.length) await sleep(gapMs);
  }
  return n;
};
